import { useEffect, useState } from "react";
import { UserCog, UserPlus, Trash2, ShieldCheck, UserCheck, UserX, Ban, CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../api";

const EMPTY = { name: "", email: "", password: "", role: "front_desk" };
const ROLE_LABELS = { admin: "Clinic Admin", clinic_manager: "Clinic Manager", front_desk: "Front Desk", assistant: "Assistant" };
const LEVEL_STYLE = {
  super_admin: "bg-pine-deep text-gold",
  owner: "bg-gold-soft/70 text-pine-deep",
  admin: "bg-teal-soft text-teal-brand",
  clinic_manager: "bg-blue-50 text-blue-600",
  manager: "bg-blue-50 text-blue-600",
  front_desk: "bg-cream text-pine/70",
  assistant: "bg-cream text-pine/70",
};

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const [approveRole, setApproveRole] = useState({});
  const [approvedCreds, setApprovedCreds] = useState(null);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const level = me.level || "owner";
  const isOwner = ["owner", "super_admin", "admin"].includes(level);
  const isSuper = level === "super_admin";

  const load = () => {
    api.get("/connect/list/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/connect/users").then((r) => setUsers(r.data)).catch(() => {});
    if (isSuper) api.get("/connect/access-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/connect/access-requests/${id}/approve`, { role: approveRole[id] || "clinic_manager" });
      setApprovedCreds(data);
      toast.success(`Approved — account created for ${data.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not approve");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/connect/access-requests/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reject");
    }
  };

  const assignRole = async (email, role) => {
    try {
      await api.patch(`/connect/users/${email}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign role");
    }
  };

  const toggleDisable = async (email, disabled) => {
    try {
      await api.patch(`/connect/users/${email}/disable`, { disabled });
      toast.success(disabled ? "Account disabled" : "Account enabled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update account");
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/connect/users", form);
      toast.success(`Account created for ${form.name}`);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (email) => {
    try {
      await api.delete(`/connect/users/${email}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove account");
    }
  };

  return (
    <div data-testid="connect-staff" className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-semibold text-pine-deep">Staff Management</h2>
        <p className="text-sm text-slate-500">Your clinic team and their Dyntra Connect access.</p>
      </div>

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <p className="px-5 pt-5 pb-3 text-[11px] uppercase tracking-wider font-bold text-slate-400">Team Roster</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3 font-bold">Name</th>
                <th className="px-5 py-3 font-bold">Role</th>
                <th className="px-5 py-3 font-bold">Shift</th>
                <th className="px-5 py-3 font-bold">Today</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-b border-pine/5 last:border-0 hover:bg-cream/50">
                  <td className="px-5 py-3.5 font-bold text-pine-deep">{s.name}</td>
                  <td className="px-5 py-3.5 text-slate-600">{s.role}</td>
                  <td className="px-5 py-3.5 text-slate-600">{s.shift}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${s.present ? "bg-teal-soft text-teal-brand" : "bg-slate-100 text-slate-500"}`}>
                      {s.present ? "Present" : "On Leave"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isSuper && (
        <div data-testid="access-requests" className="rounded-2xl bg-white border border-gold/40 shadow-soft p-6">
          <div className="flex items-center gap-2.5 mb-1">
            <UserCheck className="h-5 w-5 text-gold" />
            <h3 className="font-display text-lg font-semibold text-pine-deep">Pending Access Requests</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5">Sign-up requests waiting for Super Admin approval. Approve to create the account with the role you assign.</p>

          {approvedCreds && (
            <div data-testid="approved-creds" className="mb-5 rounded-xl bg-teal-soft border border-teal-brand/30 p-4 flex flex-wrap items-center gap-4">
              <CheckCircle2 className="h-5 w-5 text-teal-brand shrink-0" />
              <div className="text-sm">
                <p className="font-bold text-pine-deep">
                  {approvedCreds.credentials_emailed
                    ? `Account created — login details emailed to ${approvedCreds.email}.`
                    : "Account created — share these credentials manually:"}
                </p>
                <p className="text-slate-600">Email: <span className="font-bold">{approvedCreds.email}</span> · Role: {approvedCreds.role}</p>
                <p className="text-xs text-slate-500 mt-1">Backup temp password: {approvedCreds.password}</p>
              </div>
              <button onClick={() => { navigator.clipboard?.writeText(`Email: ${approvedCreds.email} | Password: ${approvedCreds.password}`); toast.success("Copied"); }} className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-bold text-pine border border-pine/15">
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          )}

          <div className="space-y-3">
            {requests.filter((r) => r.status === "pending").length === 0 && (
              <p className="text-sm text-slate-400">No pending requests.</p>
            )}
            {requests.filter((r) => r.status === "pending").map((r) => (
              <div key={r.id} data-testid={`access-request-${r.id.slice(0, 8)}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-pine/8 px-4 py-3.5">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-bold text-pine-deep">{r.name} <span className="text-xs font-medium text-slate-500">· {r.clinic}</span></p>
                  <p className="text-xs text-slate-500">{r.email} · {r.phone} · asked for <span className="font-semibold text-teal-brand">{r.role}</span></p>
                </div>
                <select
                  data-testid={`approve-role-${r.id.slice(0, 8)}`}
                  value={approveRole[r.id] || "clinic_manager"}
                  onChange={(e) => setApproveRole({ ...approveRole, [r.id]: e.target.value })}
                  className="h-10 rounded-xl border border-pine/15 px-3 text-xs font-bold bg-white focus:outline-none focus:ring-2 focus:ring-pine"
                >
                  {Object.entries(ROLE_LABELS).filter(([k]) => k !== "manager").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <button data-testid={`approve-${r.id.slice(0, 8)}`} onClick={() => approve(r.id)} className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2 text-xs font-bold text-ivory hover:bg-pine-deep transition-colors">
                  <UserCheck className="h-3.5 w-3.5" /> Approve
                </button>
                <button data-testid={`reject-${r.id.slice(0, 8)}`} onClick={() => reject(r.id)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50 transition-colors">
                  <UserX className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft p-6">
        <div className="flex items-center gap-2.5 mb-1">
          <ShieldCheck className="h-5 w-5 text-teal-brand" />
          <h3 className="font-display text-lg font-semibold text-pine-deep">Team Accounts</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          Give staff their own Dyntra Connect login. Front Desk and Assistant accounts can view and manage day-to-day work but cannot delete records.
        </p>

        <div className="space-y-3 mb-6">
          {users.map((u) => (
            <div key={u.email} data-testid={`team-user-${u.email}`} className={`flex flex-wrap items-center gap-4 rounded-xl border px-4 py-3 ${u.disabled ? "border-red-200 bg-red-50/40 opacity-70" : "border-pine/8"}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-soft text-teal-brand font-display text-sm font-semibold">
                {u.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-bold text-pine-deep">
                  {u.name}{" "}
                  <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${LEVEL_STYLE[u.level] || LEVEL_STYLE.assistant}`}>
                    {u.role}
                  </span>
                  {u.disabled && <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-500">Disabled</span>}
                </p>
                <p className="text-xs text-slate-500">{u.email}</p>
              </div>
              {isSuper && !["super_admin", "owner"].includes(u.level) && (
                <select
                  data-testid={`role-select-${u.email}`}
                  value={u.level}
                  onChange={(e) => assignRole(u.email, e.target.value)}
                  className="h-9 rounded-xl border border-pine/15 px-2.5 text-xs font-bold bg-white focus:outline-none focus:ring-2 focus:ring-pine"
                >
                  {Object.entries(ROLE_LABELS).filter(([k]) => k !== "manager").map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )}
              {isSuper && !["super_admin", "owner"].includes(u.level) && (
                <button data-testid={`disable-${u.email}`} onClick={() => toggleDisable(u.email, !u.disabled)} className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${u.disabled ? "text-teal-brand hover:text-pine" : "text-amber-600 hover:text-amber-700"}`}>
                  <Ban className="h-3.5 w-3.5" /> {u.disabled ? "Enable" : "Disable"}
                </button>
              )}
              {isOwner && !["owner", "super_admin"].includes(u.level) && (
                <button data-testid={`team-remove-${u.email}`} onClick={() => removeUser(u.email)} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-red-500 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {isOwner ? (
          <form data-testid="team-add-form" onSubmit={addUser} className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <input required minLength={2} data-testid="team-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
            <input required type="email" data-testid="team-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
            <input required minLength={8} type="text" data-testid="team-password" placeholder="Password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
            <select data-testid="team-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-11 rounded-xl border border-pine/15 px-3.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-pine">
              {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button disabled={busy} data-testid="team-submit" className="h-11 rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <UserPlus className="h-4 w-4" /> {busy ? "Creating..." : "Create Account"}
            </button>
          </form>
        ) : (
          <p className="text-xs text-slate-400">Only the Clinic Owner can create team accounts.</p>
        )}
      </div>

      <div className="rounded-2xl bg-gold-soft/40 border border-gold/30 p-5 flex items-start gap-3">
        <UserCog className="h-5 w-5 text-gold shrink-0 mt-0.5" />
        <p className="text-sm text-pine-deep/80">
          Team members sign in at the same Dyntra Connect page with their own email and password. Role limits: only Owner and Manager can delete patients, invoices and treatments; only the Owner can manage team accounts.
        </p>
      </div>
    </div>
  );
}
